// Phase E1's meta layer in the sim (gate E1, decision 0060): @rpg/meta's achievements and codex as one kernel module on
// the joined valley (a world addon), with no browser imports (the Node tools build it too).
//
// Events in, data out (the seams' item 1): the module turns the kernel's events into the meta layer's records and
// feeds them to the achievements and the codex, whose rules are tables:
//   combat:defeated  → defeated { kind, by }        (an enemy's kind, from the fight)
//   (its own scan)    → met { kind }                 (an enemy alert to the hero within 25 m, once per enemy)
//   world:discovered → discovered { poi, mark }
//   story:quest      → quest { quest, state, kind }  (kind: the quests table's main or side)
//   item:gained      → item { item, count, source }
//   progress:level   → level { level }
//   dialogue:line    → line { id, speaker }          (lore from dialogue)
//   (its own scan)    → page { page }                (a lore page within reach of the hero)
// Conditions go through the story's `Conditions` (quest.*, item.glyph, …), where the module also registers the codex's
// facts (`codex.<section>.found|total|complete`, `codex.entry.<id>`) and the achievements' (`achievement.<id>`).
// Out: `meta:unlocked` and `meta:found` on the kernel bus (the notice, telemetry, audio may listen).
// The achievements and the codex are the player's, not the save's: they live in the meta profile (src/meta/profile.ts,
// beside the settings, synced with a merge), outside the sim's save and its hash, so loading an older save never takes
// an unlock back. The module only reads the sim; it changes nothing in it.
import { Achievements, Codex, type MetaValue } from "@rpg/meta";
import type { Conditions } from "@rpg/story";
import type { GameModule } from "@rpg/kernel";
import type { GameEvents } from "../core";
import type { Fight } from "../enemies/fight";
import { HERO_ID } from "../enemies/fight";
import type { MetaContent } from "./content";

/** How far an alert enemy may be and still count as met (m), and the scans' cadence (steps). */
const MET_RANGE = 25;
const SCAN_EVERY = 12;
const CHECK_EVERY = 30;

/** What the layer reads. */
export interface MetaHost {
  content: MetaContent;
  fight: () => Fight;
  hero(): { x: number; y: number; z: number };
  /** The one condition language (the story's); the codex's and achievements' facts are added to it. */
  conditions: Conditions;
}

/** A record fed to the layer (for the handle). */
export interface MetaFed {
  kind: string;
  data: Record<string, MetaValue>;
}

/** Build the meta layer. */
export function createMetaLayer(h: MetaHost) {
  const c = h.content;
  const achievements = new Achievements(c.achievements, { test: (cond) => h.conditions.test(cond) });
  const codex = new Codex(c.codex, c.rules);
  h.conditions.register(codex.facts("codex"));
  h.conditions.register(achievements.facts("achievement"));
  const fed: MetaFed[] = [];
  let emit: (<K extends keyof GameEvents>(name: K, payload: GameEvents[K]) => void) | null = null;
  const met = new Set<number>();
  let steps = 0;

  /** Feed a record to the codex and the achievements. */
  const record = (kind: string, data: Record<string, MetaValue>): void => {
    fed.push({ kind, data });
    if (fed.length > 128) fed.splice(0, fed.length - 96);
    codex.record(kind, data);
    achievements.record(kind, data);
  };
  achievements.onUnlock((id) => emit?.("meta:unlocked", { id }));
  codex.onFound((entry) => emit?.("meta:found", { entry, section: codex.entry(entry).section }));

  const module: GameModule<GameEvents> = {
    id: "meta",
    version: "0.1.0",
    // checked declarations (decision 0077): what it emits
    provides: { events: ["meta:unlocked", "meta:found"] },
    setup(ctx) {
      emit = (name, payload) => ctx.events.emit(name, payload);
      ctx.events.on("combat:defeated", (e) => {
        const en = h.fight().state().enemies.find((x) => x.id === e.entity);
        if (en) record("defeated", { kind: en.kind, by: e.by === HERO_ID ? "hero" : "other" });
      });
      ctx.events.on("world:discovered", (e) => record("discovered", { poi: e.poi, mark: e.mark }));
      ctx.events.on("story:quest", (e) => record("quest", { quest: e.quest, state: e.state, kind: c.questKinds.get(e.quest) ?? "side" }));
      ctx.events.on("item:gained", (e) => record("item", { item: e.item, count: e.count, source: e.source }));
      ctx.events.on("progress:level", (e) => record("level", { level: e.level }));
      ctx.events.on("dialogue:line", (e) => record("line", { id: e.stringId, speaker: e.speaker }));
      ctx.registerSystem(
        () => {
          steps++;
          if (steps % SCAN_EVERY === 0) {
            const hero = h.hero();
            // enemies met: alert to the hero within range, once each (an id forgotten once it is gone)
            const live = new Set<number>();
            for (const e of h.fight().state().enemies) {
              live.add(e.id);
              if (!e.alive || met.has(e.id) || e.percept.level !== "alert") continue;
              if (Math.hypot(e.pos[0] - hero.x, e.pos[2] - hero.z) > MET_RANGE) continue;
              met.add(e.id);
              record("met", { kind: e.kind });
            }
            for (const id of met) if (!live.has(id)) met.delete(id);
            // lore pages within reach
            for (const p of c.pages) {
              if (codex.entry(p.id).found) continue;
              if (Math.hypot(p.x - hero.x, p.z - hero.z) <= p.reach) record("page", { page: p.id });
            }
          }
          if (steps % CHECK_EVERY === 0) achievements.check();
        },
        { name: "meta:watch", order: 175 },
      );
    },
  };

  return {
    module,
    achievements,
    codex,
    record,
    /** Check the achievements' conditions now (a page after a profile load, a test). */
    check: (): string[] => achievements.check(),
    /** The last records fed (for the handle). */
    fed: (): MetaFed[] => fed.map((r) => ({ kind: r.kind, data: { ...r.data } })),
    /** The lore pages and whether each is found. */
    pages: () => c.pages.map((p) => ({ ...p, found: codex.entry(p.id).found })),
    content: c,
  };
}

/** The meta layer. */
export type MetaLayer = ReturnType<typeof createMetaLayer>;
